import type { SourceRegistryEntry } from '@urbanrats/contracts';
import { fromUtcInstant } from './time.ts';

export type SourcePurpose = 'LINE_CROSSING' | 'DISPLAY';
export type MoneyMode = 'DEMO' | 'REAL';

export type SourceBlockReason =
  | 'SNAPSHOT_NOT_SUPPORTED'
  | 'REJECTED'
  | 'RIGHTS_PENDING'
  | 'QUARANTINED'
  | 'RIGHTS_EXPIRED'
  | 'DISPLAY_NOT_PERMITTED'
  | 'CV_NOT_PERMITTED'
  | 'NO_ORIGIN'
  | 'NOT_APPROVED_FOR_REAL_MONEY'
  | 'COMMERCIAL_NOT_PERMITTED'
  | 'TIMING_UNTRUSTED'
  | 'CLOCK_BOUND_UNKNOWN'
  | 'NOT_LIVE';

/**
 * Whether a registry entry may be used for a purpose. "Publicly reachable" is never enough:
 * rights, media capability, timing trust and money mode all gate selection.
 */
export function sourceEligibility(
  e: SourceRegistryEntry,
  ctx: { nowMs: number; purpose: SourcePurpose; money: MoneyMode },
): { selectable: boolean; reasons: SourceBlockReason[] } {
  const r: SourceBlockReason[] = [];
  if (e.readiness === 'REJECTED') r.push('REJECTED');
  if (e.readiness === 'RIGHTS_PENDING') r.push('RIGHTS_PENDING');
  if (e.readiness === 'QUARANTINED') r.push('QUARANTINED');
  if (e.readiness === 'UNSUPPORTED_SNAPSHOT' || (ctx.purpose === 'LINE_CROSSING' && e.mediaType === 'STILL_IMAGE')) r.push('SNAPSHOT_NOT_SUPPORTED');
  if (e.rightsExpiresAt && fromUtcInstant(e.rightsExpiresAt) <= ctx.nowMs) r.push('RIGHTS_EXPIRED');
  if (!e.permittedUses.includes('DISPLAY')) r.push('DISPLAY_NOT_PERMITTED');
  if (ctx.purpose === 'LINE_CROSSING' && !e.permittedUses.includes('CV_PROCESSING')) r.push('CV_NOT_PERMITTED');
  if (e.origin === null && e.mediaType !== 'VIDEO_WEBCAM') r.push('NO_ORIGIN');
  if (ctx.money === 'REAL') {
    if (e.readiness !== 'APPROVED') r.push('NOT_APPROVED_FOR_REAL_MONEY');
    if (!e.permittedUses.includes('COMMERCIAL')) r.push('COMMERCIAL_NOT_PERMITTED');
    if (e.captureClockTrust !== 'TRUSTED') r.push('TIMING_UNTRUSTED');
    if (e.captureErrorBoundMs === null) r.push('CLOCK_BOUND_UNKNOWN');
    if (e.sourceMode !== 'LIVE' && e.sourceMode !== 'DELAYED_LIVE') r.push('NOT_LIVE');
  }
  return { selectable: r.length === 0, reasons: [...new Set(r)] };
}

export type EpochReason = 'LOAD' | 'SEEK' | 'LOOP' | 'RESTART' | 'RECONNECT' | 'SOURCE_CHANGE';

/**
 * A source epoch is a continuous stretch of media timeline. PTS values are only comparable inside one
 * epoch; any discontinuity (seek, loop, restart, reconnect) starts a new epoch, and a round bound to an
 * old epoch must not be settled from new-epoch frames.
 */
export class SourceEpochTracker {
  private n = 0;
  private id: string;
  readonly history: { epoch: string; reason: EpochReason }[] = [];

  private readonly sourceId: string;

  constructor(sourceId: string) {
    this.sourceId = sourceId;
    this.id = this.next('LOAD');
  }

  current(): string {
    return this.id;
  }

  bump(reason: EpochReason): string {
    this.id = this.next(reason);
    return this.id;
  }

  private next(reason: EpochReason): string {
    const id = `${this.sourceId}#${++this.n}`;
    this.history.push({ epoch: id, reason });
    return id;
  }
}

export type PlayerPhase = 'idle' | 'loading' | 'playing' | 'buffering' | 'paused' | 'ended' | 'error';

export interface MediaObservation {
  phase: PlayerPhase;
  nowMs: number;
  /** Wall time (client) when the last new frame was presented. */
  lastFrameAtMs: number | null;
  /** When the current buffering spell began. */
  bufferingSinceMs: number | null;
  /** Estimated age of the presented picture; null when the capture clock is unknown. */
  mediaAgeMs: number | null;
  maxSourceAgeMs: number | null;
}

export type MediaHealthReason = 'NOT_PLAYING' | 'ERROR' | 'BUFFERING' | 'STALLED' | 'FROZEN' | 'SOURCE_TOO_OLD' | 'AGE_UNKNOWN';

export interface MediaHealth {
  status: 'ONLINE' | 'DEGRADED' | 'OFFLINE';
  reasons: MediaHealthReason[];
  /** New bets may be accepted only while the picture is healthy. */
  bettable: boolean;
}

export const MEDIA_THRESHOLDS = { stallMs: 2_000, frozenMs: 3_000 } as const;

export function evaluateMediaHealth(o: MediaObservation, t = MEDIA_THRESHOLDS): MediaHealth {
  const reasons: MediaHealthReason[] = [];
  if (o.phase === 'error') return { status: 'OFFLINE', reasons: ['ERROR'], bettable: false };
  if (o.phase !== 'playing' && o.phase !== 'buffering') return { status: 'OFFLINE', reasons: ['NOT_PLAYING'], bettable: false };
  let status: MediaHealth['status'] = 'ONLINE';
  if (o.phase === 'buffering') {
    const long = o.bufferingSinceMs !== null && o.nowMs - o.bufferingSinceMs >= t.stallMs;
    reasons.push(long ? 'STALLED' : 'BUFFERING');
    status = long ? 'OFFLINE' : 'DEGRADED';
  } else if (o.lastFrameAtMs === null || o.nowMs - o.lastFrameAtMs >= t.frozenMs) {
    // "Playing" but no new frame: a frozen picture must not look live.
    reasons.push('FROZEN');
    status = 'OFFLINE';
  }
  if (o.mediaAgeMs === null) reasons.push('AGE_UNKNOWN');
  else if (o.maxSourceAgeMs !== null && o.mediaAgeMs > o.maxSourceAgeMs) {
    reasons.push('SOURCE_TOO_OLD');
    if (status === 'ONLINE') status = 'DEGRADED';
  }
  return { status, reasons, bettable: status === 'ONLINE' };
}
